/**
 * MDF-209 / MED-1308: the saved table layout of one Data Library tab.
 *
 * A small plain object, so a saved view (MDF-38) can store the same shape in
 * the database later. This module has no React code, so a node script can
 * test it.
 */
export interface StoredColumn {
  id: string;
  visible: boolean;
}

export interface ColumnState {
  /** Column order and visibility, in display order */
  columns?: StoredColumn[];
  /** Width in px per column id, set by drag. A missing id uses the default. */
  widths?: Record<string, number>;
}

export const STORAGE_PREFIX = 'medley.dataLibrary.v1.';

// The narrowest and widest a Data Library column can get by drag.
export const MIN_COLUMN_WIDTH = 80;
export const MAX_COLUMN_WIDTH = 800;

export function clampWidth(width: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, Math.round(width)));
}

/** Reads untrusted JSON from storage. Drops every part that has the wrong shape. */
export function parseColumnState(raw: string | null): ColumnState {
  if (!raw) return {};
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return {};
  }
  if (!parsed || typeof parsed !== 'object') return {};
  const { columns, widths } = parsed as Record<string, unknown>;
  const state: ColumnState = {};
  if (Array.isArray(columns)) {
    state.columns = columns.filter(
      (c): c is StoredColumn =>
        !!c && typeof c.id === 'string' && typeof c.visible === 'boolean',
    );
  }
  if (widths && typeof widths === 'object') {
    state.widths = {};
    for (const [id, value] of Object.entries(widths)) {
      if (typeof value === 'number' && Number.isFinite(value)) {
        state.widths[id] = clampWidth(value, MIN_COLUMN_WIDTH, MAX_COLUMN_WIDTH);
      }
    }
  }
  return state;
}

/**
 * Applies the saved order and visibility to the current columns. A saved id
 * that no longer exists is ignored. A column that was not saved keeps its
 * default visibility and goes in after the column before it in the default
 * order.
 */
export function mergeColumns<C extends StoredColumn>(
  defaults: C[],
  stored: StoredColumn[] | undefined,
): C[] {
  if (!stored || stored.length === 0) return defaults;
  const byId = new Map(defaults.map(c => [c.id, c]));
  const result: C[] = [];
  const placed = new Set<string>();
  for (const s of stored) {
    const col = byId.get(s.id);
    if (!col || placed.has(s.id)) continue;
    // defaultVisible lets "reset" in the column popover restore the code default.
    result.push({ ...col, visible: s.visible, defaultVisible: col.visible });
    placed.add(s.id);
  }
  defaults.forEach((col, i) => {
    if (placed.has(col.id)) return;
    const at = i === 0 ? 0 : result.findIndex(c => c.id === defaults[i - 1].id) + 1;
    result.splice(at, 0, col);
    placed.add(col.id);
  });
  return result;
}

/**
 * The value to save after a change. Saved columns that are not in the current
 * list (questions of a cohort that is not selected now) stay saved.
 */
export function toStoredColumns(
  next: StoredColumn[],
  previous: StoredColumn[] | undefined,
): StoredColumn[] {
  const ids = new Set(next.map(c => c.id));
  return [
    ...next.map(({ id, visible }) => ({ id, visible })),
    ...(previous ?? []).filter(c => !ids.has(c.id)),
  ];
}

/** A cell style with a set width. The width pins the column (min and max). */
export function withColumnWidth<S extends object>(
  style: S,
  width: number | undefined,
): S | (S & { width: number; minWidth: number; maxWidth: number }) {
  return width === undefined
    ? style
    : { ...style, width, minWidth: width, maxWidth: width };
}

export function readStorage(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

export function writeStorage(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // Storage can be full or blocked. The layout then lasts for this visit.
  }
}

/** Reads the saved state fresh, applies the change, and saves it again. */
export function updateColumnState(
  key: string,
  update: (state: ColumnState) => ColumnState,
): void {
  writeStorage(key, JSON.stringify(update(parseColumnState(readStorage(key)))));
}

// MDF-223: the cohort panel on the left of the Data Library.
export const SIDE_PANEL_DEFAULT_WIDTH = 300;
export const SIDE_PANEL_MIN_WIDTH = 240;
export const SIDE_PANEL_MAX_WIDTH = 640;

export function parseSidePanelWidth(raw: string | null): number {
  const width = raw ? Number(raw) : NaN;
  return Number.isFinite(width)
    ? clampWidth(width, SIDE_PANEL_MIN_WIDTH, SIDE_PANEL_MAX_WIDTH)
    : SIDE_PANEL_DEFAULT_WIDTH;
}
